import {
  SidebarFooterLinks,
  type SidebarItemConfig,
  SidebarSeparator,
} from '@this+that/ui';
import { TFunction } from 'i18next';
import { FiCreditCard } from 'react-icons/fi';
import {
  LuBrain,
  LuCalendar,
  LuHeadphones,
  LuHouse,
  LuShuffle,
  LuUnplug,
  LuUsers,
} from 'react-icons/lu';
import { TbSparkles } from 'react-icons/tb';

import {
  DoBoxNavLink,
  HiUser,
  InboxSection,
  SidebarNavLink,
  ThemeToggle,
} from './components';
import { useHasActivePaidPlan, useHiUser } from './hooks';
import { InboxType } from './types';
import { APP_PAGES } from '../../routes/constants';
import { Platform, usePlatformStore } from '../../store';
import { pushGTMEvent } from '../../utils/analytics/gtm';
import { PRIVACY_POLICY_URL } from '../auth';

export const useSidebarConfig = (t: TFunction) => {
  const { platformSelected } = usePlatformStore();
  const hiUserProps = useHiUser();
  const hasActivePaidPlan = useHasActivePaidPlan();

  const getInboxSectionItems = (): Array<{
    type: InboxType;
    showCounter?: boolean;
  }> => {
    if (platformSelected === Platform.TEAMS) {
      return [{ type: 'teams-chats' }];
    }

    if (platformSelected === Platform.INSTAGRAM) {
      return [{ type: 'instagram-dms' }];
    }

    if (platformSelected === Platform.SLACK) {
      return [{ type: 'slack-directs' }, { type: 'slack-channels' }];
    }

    return [
      { type: 'main' },
      { type: 'sent' },
      { type: 'draft' },
      { type: 'labels', showCounter: false },
      { type: 'all' },
      { type: 'spam' },
      { type: 'trash' },
    ];
  };

  const sidebarItems: SidebarItemConfig[] = [
    {
      id: 'dobox',
      component: DoBoxNavLink,
      props: {
        icon: LuHouse,
        text: t('common.sidebar.doBox'),
        href: APP_PAGES.DOBOX,
      },
    },
    {
      id: 'separator-2',
      component: SidebarSeparator,
      props: {},
    },
    {
      id: 'inbox-section',
      component: InboxSection,
      props: {
        items: getInboxSectionItems(),
      },
    },
    {
      id: 'separator-3',
      component: SidebarSeparator,
      props: {},
    },
    {
      id: 'flows',
      component: SidebarNavLink,
      props: {
        icon: LuShuffle,
        text: 'common.sidebar.flows',
        href: APP_PAGES.FLOWS,
      },
    },
    {
      id: 'brain',
      component: SidebarNavLink,
      props: {
        icon: LuBrain,
        text: 'common.sidebar.brain',
        href: APP_PAGES.BRAIN,
      },
    },
    {
      id: 'assistant',
      component: SidebarNavLink,
      props: {
        icon: TbSparkles,
        text: 'common.sidebar.assistant',
        href: APP_PAGES.ASSISTANT,
        matchNestedRoutes: true,
      },
    },
    {
      id: 'calendar',
      component: SidebarNavLink,
      props: {
        icon: LuCalendar,
        text: 'common.sidebar.calendar',
        href: APP_PAGES.CALENDAR,
      },
    },
    {
      id: 'contacts',
      component: SidebarNavLink,
      props: {
        icon: LuUsers,
        text: 'common.sidebar.contacts',
        href: APP_PAGES.CONTACTS,
      },
    },
    {
      id: 'integration',
      component: SidebarNavLink,
      props: {
        icon: LuUnplug,
        text: 'common.sidebar.integration',
        href: APP_PAGES.INTEGRATION,
      },
    },
    {
      id: 'support',
      component: SidebarNavLink,
      props: {
        icon: LuHeadphones,
        text: 'common.sidebar.support',
        href: APP_PAGES.SUPPORT,
        onNavigate: () => pushGTMEvent({ event: 'support_sidebar_clicked' }),
      },
    },
    ...(hasActivePaidPlan
      ? []
      : [
          {
            id: 'billing',
            component: SidebarNavLink,
            props: {
              icon: FiCreditCard,
              text: 'common.sidebar.billing',
              href: APP_PAGES.BILLING,
            },
          },
        ]),
    {
      id: 'separator-4',
      component: SidebarSeparator,
      props: {},
    },
  ];

  const footerItems: SidebarItemConfig[] = [
    {
      id: 'theme-toggle',
      component: ThemeToggle,
      props: {},
    },
    ...(hiUserProps
      ? [
          {
            id: 'hiuser',
            component: HiUser,
            props: hiUserProps,
          },
        ]
      : []),
    {
      id: 'sidebarfooter',
      component: SidebarFooterLinks,
      props: {
        links: [
          {
            label: t('common.sidebar.terms'),
            href: 'https://www.thisandthat.chat/terms-of-service',
          },
          {
            label: t('common.sidebar.policy'),
            href: PRIVACY_POLICY_URL,
          },
        ],
        separatorLabel: t('common.sidebar.dotSeparator'),
      },
      requiresOpen: true,
    },
  ];

  return { sidebarItems, footerItems };
};
